// Robot Module - Hooks
import { useState, useCallback, useRef } from 'react';
import { Robot, RobotStats } from '../types';
import { nhbApiService } from '../services/nhbApiService';

type RefreshOptions = {
  silent?: boolean;
};

export const useRobots = () => {
  const [robots, setRobots] = useState<Robot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestIdRef = useRef(0);

  const areRobotsEquivalent = (a: Robot[], b: Robot[]) => {
    if (a === b) return true;
    if (a.length !== b.length) return false;

    for (let i = 0; i < a.length; i++) {
      const ra = a[i];
      const rb = b[i];
      if (
        ra.robot_id !== rb.robot_id ||
        ra.name !== rb.name ||
        ra.museum_code !== rb.museum_code ||
        ra.museum_name !== rb.museum_name ||
        ra.status !== rb.status ||
        ra.is_online !== rb.is_online ||
        ra.battery_level !== rb.battery_level ||
        ra.battery_status !== rb.battery_status ||
        ra.current_tour !== rb.current_tour ||
        ra.health_status !== rb.health_status ||
        ra.last_seen !== rb.last_seen ||
        ra.state !== rb.state ||
        ra.timestamp !== rb.timestamp
      ) {
        return false;
      }

      const la = ra.location;
      const lb = rb.location;
      if (
        la?.x !== lb?.x ||
        la?.y !== lb?.y ||
        la?.floor !== lb?.floor ||
        la?.zone !== lb?.zone ||
        la?.heading !== lb?.heading ||
        la?.last_updated !== lb?.last_updated ||
        la?.tilt !== lb?.tilt
      ) {
        return false;
      }
    }

    return true;
  };

  const fetchRobotsForMuseum = useCallback(
    async (museumCode?: string, museumName?: string, options?: RefreshOptions) => {
      const silent = options?.silent ?? false;
      const requestId = ++requestIdRef.current;

      try {
        if (!silent) setLoading(true);
        setError(null);

        // Use real NHB API
        let nhbRobots;
        if (museumCode && museumName) {
          nhbRobots = await nhbApiService.getRobotsByMuseum(museumCode);
        } else {
          // Fetch all robots for "All Museums"
          nhbRobots = await nhbApiService.getRobotsStatus();
        }

        // Map NHB robots to our internal format
        const mappedRobots = nhbRobots.map(nhbRobot => {
          // Try to find the museum name from museum code, or use the museum code itself
          const robotMuseumName = museumName || nhbRobot.museum;
          return nhbApiService.mapToInternalRobot(nhbRobot, robotMuseumName);
        });

        // Keep ordering stable to reduce UI churn (e.g., tab indices, map iframe)
        mappedRobots.sort((a, b) => a.robot_id.localeCompare(b.robot_id));

        // Ignore late responses from older requests
        if (requestId !== requestIdRef.current) return;

        setRobots(prev => (areRobotsEquivalent(prev, mappedRobots) ? prev : mappedRobots));
      } catch (err) {
        if (requestId !== requestIdRef.current) return;

        setError('Failed to fetch robots from NHB API');
        console.error('Error fetching robots:', err);

        // During silent/background refresh, keep last known data.
        if (!silent) {
          setRobots([]);
        }
      } finally {
        if (requestId === requestIdRef.current && !silent) {
          setLoading(false);
        }
      }
    },
    [],
  );

  const refreshRobots = useCallback(
    async (museumCode?: string, museumName?: string, options?: RefreshOptions) => {
      await fetchRobotsForMuseum(museumCode, museumName, options);
    },
    [fetchRobotsForMuseum],
  );

  const getRobotStats = useCallback((): RobotStats => {
    const stats: RobotStats = {
      total: robots.length,
      online: robots.filter(robot => robot.is_online && robot.status === 'idle').length,
      busy: robots.filter(robot => robot.is_online && (robot.status === 'busy' || robot.status === 'executing')).length,
      charging: robots.filter(robot => robot.is_online && robot.status === 'charging').length,
      offline: robots.filter(robot => !robot.is_online || robot.status === 'error').length,
    };

    // Add robots that are online but not in idle/busy/charging states to online count
    const onlineButNotCategorized = robots.filter(
      robot => robot.is_online && !['idle', 'busy', 'executing', 'charging'].includes(robot.status.toLowerCase()),
    ).length;

    stats.online += onlineButNotCategorized;

    return stats;
  }, [robots]);

  const getRobotById = useCallback(
    (robotId: string): Robot | undefined => {
      return robots.find(robot => robot.robot_id === robotId);
    },
    [robots],
  );

  return {
    robots,
    loading,
    error,
    fetchRobotsForMuseum,
    refreshRobots,
    getRobotStats,
    getRobotById,
  };
};
